import { LeftOutlined, PrinterOutlined } from '@ant-design/icons'
import { Button, Flex, Form } from 'antd'
import { useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import { useStocksServiceGetInternalReturnEndpoint } from 'src/api/queries'
import { LoadingScreen, PageBreadcrumbs } from 'src/components'
import { LocalReturnDetails } from 'src/features/stock-local/components'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { toDayjs } from 'src/utils'

export const LocalStockReturnsDetailsPage = () => {
  const { t } = useLocales()
  const { id } = useParams()
  const [form] = Form.useForm()
  const navigate = useNavigate()

  const printableRef = useRef<HTMLDivElement>(null)

  const { data, isLoading } = useStocksServiceGetInternalReturnEndpoint(
    { id: String(id) },
    [id],
    {
      enabled: !!id
    }
  )

  const onBack = () => {
    navigate(paths.stockLocal.returns.root)
  }

  const onPrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `Stock_Return_${id}`
  })

  useEffect(() => {
    if (id && data) {
      const existingFileIds = data?.mediaFiles
        ?.map((file) => file?.id)
        .filter(Boolean) as string[]

      form.setFieldsValue({
        invoiceDate: toDayjs(data?.documentDate),
        comment: data?.comment,
        medicine: data?.items?.map((item) => ({
          barCode: item?.barCode,
          name: `${item?.internationalName?.name} ${item.internationalName?.dosage}`,
          productName: item?.product?.name,
          producerName: item?.producer?.name,
          expirationDate: toDayjs(item?.expirationDate)?.format(
            yearMonthDaySlash
          ),
          quantity: `${item?.quantity}`,
          unitPrice: item?.unitPrice,
          priceTotal: Number(item?.quantity) * Number(item?.unitPrice)
        })),
        docs: existingFileIds?.map((id) => ({
          uid: id,
          name: `File-${id}`,
          status: 'done'
        }))
      })
    }
  }, [data, id])

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={`${t('stock_returns.stock_return')}: ${data?.code}`}
      items={[
        { title: t('menu.consumable_medicaments') },
        { title: t('stock.returns'), path: paths.stockLocal.returns.root },
        { title: `${t('stock_returns.stock_return')}: ${data?.code}` }
      ]}
    >
      <Flex vertical gap={16}>
        <Flex justify="space-between">
          <Button icon={<LeftOutlined />} onClick={onBack}>
            {t('common.back')}
          </Button>

          <Button icon={<PrinterOutlined />} onClick={() => onPrint()}>
            {t('stock_applications.print')}
          </Button>
        </Flex>

        <div ref={printableRef}>
          <Form
            layout="vertical"
            form={form}
            requiredMark={false}
            disabled={!!data}
          >
            <LocalReturnDetails item={data} form={form} />
          </Form>
        </div>
      </Flex>
    </PageBreadcrumbs>
  )
}
